import axios from 'axios';
import { normalizeSharePointConfigRow } from '../utils/sharePointConfigRowUtils';
import { siteStatsDebug, decodeJwtImpersonateConfigId } from '../utils/siteStatsDebug';

const withAuth = (token, extra = {}) => ({
  withCredentials: true,
  headers: {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra
  }
});

function configOwnerFields(configOwnerEmail) {
  const v = configOwnerEmail != null ? String(configOwnerEmail).trim() : '';
  return v ? { config_owner_email: v } : {};
}

class SharePointService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL;
  }

  async getConfig(token, configOwnerEmail = null) {
    try {
      const baseUrl = this.baseUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const apiUrl = `${baseUrl}/api/sharepoint/config`;

      const response = await axios.get(apiUrl, {
        ...withAuth(token),
        params: configOwnerFields(configOwnerEmail)
      });

      const data = response.data;

      if (!data) {
        return { success: false, message: 'Empty response received' };
      }

      if (data.success === false || data.message === 'No configuration found') {
        return data;
      }

      if (data.success === true && data.configs && Array.isArray(data.configs)) {
        const configsWithIds = data.configs.map((config) => {
          const norm = normalizeSharePointConfigRow(config);
          if (!norm.id && config.sharepoint_config_id) {
            norm.id = config.sharepoint_config_id;
          }
          return norm;
        });
        const allNorm = Array.isArray(data.all_configurations)
          ? data.all_configurations.map(normalizeSharePointConfigRow)
          : configsWithIds;
        return {
          success: true,
          configs: configsWithIds,
          all_configurations: allNorm
        };
      }

      if (data.id || data.tenant_url) {
        const config = {
          id: data.id || data.sharepoint_config_id || 1,
          tenant_url: data.tenant_url || '',
          client_id: data.client_id || '',
          tenant_id: data.tenant_id || '',
          client_secret: data.client_secret || '',
          additional_scopes: data.additional_scopes || ''
        };
        const configs = data.all_configurations || data.configs || [config];
        return {
          success: true,
          configs,
          all_configurations: data.all_configurations || configs
        };
      }

      if (Array.isArray(data)) {
        const configsWithIds = data.map(config => {
          if (!config.id && config.sharepoint_config_id) {
            return { ...config, id: config.sharepoint_config_id };
          }
          return config;
        });
        return {
          success: true,
          configs: configsWithIds,
          all_configurations: configsWithIds
        };
      }

      return { success: false, message: 'Unrecognized API response format' };
    } catch (error) {
      console.error('Error fetching SharePoint config:', error);
      if (process.env.NODE_ENV === 'development') {
        return { success: true, configs: [] };
      }
      throw error;
    }
  }

  async saveConfig(token, config, configOwnerEmail = null) {
    const cleanedConfig = {
      tenant_url: config.tenant_url?.trim(),
      client_id: config.client_id?.trim(),
      client_secret: config.client_secret === '********' ? null : config.client_secret?.trim(),
      tenant_id: config.tenant_id?.trim(),
      ...configOwnerFields(configOwnerEmail)
    };

    const response = await axios.post(
      `${this.baseUrl}/api/sharepoint/config`,
      cleanedConfig,
      withAuth(token, { 'Content-Type': 'application/json' })
    );
    return response.data;
  }

  async testConnection(token, config, configOwnerEmail = null) {
    const baseUrl = this.baseUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const apiUrl = `${baseUrl}/api/sharepoint/test-connection`;

    if (!config.tenant_url || !config.client_id || !config.tenant_id) {
      throw new Error('Missing required configuration parameters');
    }

    const payload = {
      tenant_url: config.tenant_url.trim(),
      client_id: config.client_id.trim(),
      client_secret: config.client_secret,
      tenant_id: config.tenant_id.trim(),
      ...configOwnerFields(configOwnerEmail)
    };

    const response = await axios.post(
      apiUrl,
      payload,
      withAuth(token, { 'Content-Type': 'application/json' })
    );
    return response.data;
  }

  async getLists(token) {
    const response = await axios.get(
      `${this.baseUrl}/api/sharepoint/lists`,
      withAuth(token)
    );
    return response.data;
  }

  async getSecurityReport(token) {
    const response = await axios.get(
      `${this.baseUrl}/api/sharepoint/security-report`,
      withAuth(token)
    );
    return response.data;
  }

  async sendReportByEmail(token, { userEmail, reportContent, fileName }) {
    await axios.post(
      `${this.baseUrl}/api/send-report`,
      { userEmail, reportContent, fileName },
      withAuth(token, { 'Content-Type': 'application/json' })
    );
  }

  async downloadReport(token, { reportContent }) {
    const response = await axios.post(
      `${this.baseUrl}/api/sharepoint/security-report/download`,
      { reportContent },
      {
        ...withAuth(token, { 'Content-Type': 'application/json' }),
        responseType: 'blob'
      }
    );
    return response.data;
  }

  async getETLLogs(token) {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/sharepoint/etl-logs`,
      withAuth(token)
    );
    return response.data;
  }

  async getSharePointStats(token, configId) {
    siteStatsDebug('StatsAPI', {
      event: 'GET /stats',
      queryConfigId: configId ?? null,
      jwtImpersonateConfigId: decodeJwtImpersonateConfigId(token),
      mismatch:
        configId != null &&
        decodeJwtImpersonateConfigId(token) != null &&
        parseInt(configId, 10) !== decodeJwtImpersonateConfigId(token),
    });
    const response = await axios.get(
      `${this.baseUrl}/api/sharepoint/stats`,
      {
        ...withAuth(token, {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Cache-Control': 'no-cache'
        }),
        params: { configId: configId }
      }
    );
    return response.data;
  }

  async getPermissionScopeReport(token, { allSites, configId, includeFolders } = {}) {
    const params = {};
    if (allSites) params.allSites = '1';
    if (configId != null && configId !== '') params.configId = configId;
    if (includeFolders === false) params.includeFolders = '0';
    const response = await axios.get(`${this.baseUrl}/api/sharepoint/stats/permission-scope`, {
      ...withAuth(token, {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache'
      }),
      params
    });
    return response.data;
  }

  async getPermissionScopeDetail(token, params = {}) {
    const response = await axios.get(`${this.baseUrl}/api/sharepoint/stats/permission-scope/detail`, {
      ...withAuth(token, {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache'
      }),
      params: {
        scope: params.scope,
        configId: params.configId,
        libraryInternalId: params.libraryInternalId,
        folderItemId: params.folderItemId,
        page: params.page,
        pageSize: params.pageSize
      }
    });
    return response.data;
  }

  async getETLStats(token, configId) {
    const response = await axios.get(
      `${this.baseUrl}/api/sharepoint/etl/stats`,
      {
        ...withAuth(token, {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Cache-Control': 'no-cache'
        }),
        params: { configId: configId }
      }
    );
    return response.data;
  }

  async getEtlStatus(token, configId) {
    const response = await axios.get(
      `${this.baseUrl}/api/sharepoint/etl/status`,
      {
        ...withAuth(token, {
          'Content-Type': 'application/json'
        }),
        params: { configId }
      }
    );
    return response.data.libraries || [];
  }

  async getDataWarehouseLists(token, configId) {
    const response = await axios.get(`${this.baseUrl}/api/datawarehouse/lists`, {
      ...withAuth(token),
      params: { configId }
    });
    return Array.isArray(response.data) ? response.data : [];
  }

  async listSites(token, configOwnerEmail = null) {
    const response = await axios.get(`${this.baseUrl}/api/sharepoint/config/sites`, {
      ...withAuth(token),
      params: configOwnerFields(configOwnerEmail)
    });
    const payload = response.data;
    if (payload && payload.success && Array.isArray(payload.data)) {
      return {
        ...payload,
        data: payload.data.map(normalizeSharePointConfigRow)
      };
    }
    return payload;
  }

  async addSubSite(token, { parentId, sitePath }, configOwnerEmail = null) {
    const res = await axios.post(
      `${this.baseUrl}/api/sharepoint/config/sites`,
      { parentId, sitePath, ...configOwnerFields(configOwnerEmail) },
      withAuth(token, { 'Content-Type': 'application/json' })
    );
    return res.data;
  }

  async updateSubSite(token, { id, sitePath }, configOwnerEmail = null) {
    const res = await axios.put(
      `${this.baseUrl}/api/sharepoint/config/sites/${id}`,
      { sitePath, ...configOwnerFields(configOwnerEmail) },
      withAuth(token, { 'Content-Type': 'application/json' })
    );
    return res.data;
  }

  async deleteSubSite(token, id, purge = false, configOwnerEmail = null) {
    const res = await axios.delete(
      `${this.baseUrl}/api/sharepoint/config/sites/${id}`,
      {
        ...withAuth(token),
        params: {
          purge: purge ? '1' : '0',
          ...configOwnerFields(configOwnerEmail)
        },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 202
      }
    );
    return res.data;
  }

  async discoverSharePointSites(token, parentId, search = '*', configOwnerEmail = null) {
    const response = await axios.get(`${this.baseUrl}/api/sharepoint/config/discover-sites`, {
      ...withAuth(token),
      params: {
        parentId,
        search: search || '*',
        ...configOwnerFields(configOwnerEmail)
      }
    });
    return response.data;
  }

  async testSitePath(token, { parentId, sitePath }, configOwnerEmail = null) {
    const response = await axios.post(
      `${this.baseUrl}/api/sharepoint/config/test-site-path`,
      { parentId, sitePath, ...configOwnerFields(configOwnerEmail) },
      withAuth(token, { 'Content-Type': 'application/json' })
    );
    return response.data;
  }
}

export const sharePointService = new SharePointService();
